/**
 * Client side of the off-thread station solvers.
 *
 * Two properties this client guarantees, both shaped by how the solver panels
 * actually use it:
 *
 * LATEST WINS, PER SOLVER. A panel only ever wants the newest answer - the
 * student moved the knob again, so the solve in flight answers a question
 * nobody is asking any more. Each solver is its own channel; issuing a new
 * request on a channel makes every older request on it resolve to null, and
 * the caller drops nulls without rendering. Requests never queue up behind a
 * slow solve, so a dragged knob costs at most one stale solve, not a backlog.
 *
 * INLINE FALLBACK. Some locked-down school networks and older classroom
 * browsers will not give us a module worker, and the project's promise is that
 * everything runs on ordinary classroom computers. Degraded there means "the
 * frame hitches", not "the feature is missing" - the same functions run on the
 * main thread instead, exactly as they did before this client existed.
 */

import { traceImplant, type McRecipe, type McResult } from './implant/montecarlo'
import {
  solveDiffusion,
  type AnnealStep,
  type DiffusionOptions,
  type DiffusionResult,
} from './thermal/diffusion'
import { solveFill, type FillGeometry, type FillRecipe, type FillResult } from './plating/frontfill'
import { planarize, type DieMap, type PlanarizeResult } from './cmp/planarize'
import type { CmpCondition, CmpRecipe } from './cmp/CmpSim'
import type { SolveError, SolveReply, SolveRequest } from './solvers.worker'

type Channel = SolveRequest['type']

export class SolverRunner {
  private worker: Worker | null = null
  private nextId = 1
  /** Newest request id per channel - anything older resolves null. */
  private readonly newest = new Map<Channel, number>()
  private readonly pending = new Map<
    number,
    { channel: Channel; resolve: (v: unknown) => void }
  >()

  /** True when work is genuinely off the main thread. */
  readonly offThread: boolean

  constructor() {
    try {
      this.worker = new Worker(new URL('./solvers.worker.ts', import.meta.url), { type: 'module' })
      this.worker.onmessage = (e: MessageEvent<SolveReply | SolveError>) => this.onMessage(e.data)
      this.worker.onerror = () => this.degrade()
      this.offThread = true
    } catch {
      this.worker = null
      this.offThread = false
    }
  }

  private degrade(): void {
    // A worker that failed to start is worse than none: resolve everything
    // in flight as superseded so callers fall through cleanly, then let every
    // future request take the inline path.
    for (const [, p] of this.pending) p.resolve(null)
    this.pending.clear()
    this.worker?.terminate()
    this.worker = null
  }

  private onMessage(msg: SolveReply | SolveError): void {
    const entry = this.pending.get(msg.id)
    if (!entry) return
    this.pending.delete(msg.id)
    if (msg.type === 'error') {
      entry.resolve(null)
      return
    }
    // A reply for anything but the channel's newest request is an answer to a
    // question the student has since changed. Dropping it HERE rather than in
    // every panel is the point of having a client at all.
    entry.resolve(this.newest.get(entry.channel) === msg.id ? msg.result : null)
  }

  private submit<T>(req: SolveRequest, inline: () => T): Promise<T | null> {
    if (!this.worker) {
      // Inline, but still latest-wins so the two paths behave identically.
      this.newest.set(req.type, req.id)
      return Promise.resolve().then(() =>
        this.newest.get(req.type) === req.id ? inline() : null,
      )
    }
    this.newest.set(req.type, req.id)
    // Anything already in flight on this channel is now superseded; resolve it
    // so no caller is left waiting on a promise that will never matter.
    for (const [id, p] of this.pending) {
      if (p.channel === req.type) {
        this.pending.delete(id)
        p.resolve(null)
      }
    }
    return new Promise<T | null>((resolve) => {
      this.pending.set(req.id, { channel: req.type, resolve: resolve as (v: unknown) => void })
      this.worker!.postMessage(req)
    })
  }

  /** Trace an implant. Null means a newer request superseded this one. */
  implant(recipe: McRecipe, ions: number): Promise<McResult | null> {
    const id = this.nextId++
    return this.submit({ type: 'implant', id, recipe, ions }, () => traceImplant(recipe, ions))
  }

  /** Solve a diffusion cycle. Null means superseded. */
  diffusion(options: DiffusionOptions, steps: AnnealStep[]): Promise<DiffusionResult | null> {
    const id = this.nextId++
    return this.submit({ type: 'diffusion', id, options, steps }, () =>
      solveDiffusion(options, steps),
    )
  }

  /** Advance a trench fill. Null means superseded. */
  fill(geometry: FillGeometry, recipe: FillRecipe): Promise<FillResult | null> {
    const id = this.nextId++
    return this.submit({ type: 'fill', id, geometry, recipe }, () => solveFill(geometry, recipe))
  }

  /** Planarise a die. Null means superseded. */
  cmp(recipe: CmpRecipe, condition: CmpCondition, die: DieMap): Promise<PlanarizeResult | null> {
    const id = this.nextId++
    return this.submit({ type: 'cmp', id, recipe, condition, die }, () =>
      planarize(recipe, condition, die),
    )
  }
}
